import { Columns, Heading, Notification } from 'react-bulma-components';
import { Interval, Note, Progression } from '@tonaljs/tonal';
import React, { useRef, useState } from 'react';
import { changeKeyIndex, getCurrentKey, getKeyIndex, getKeyTransposedBySemitones } from '../lib/transposeKey';

import InstrumentContent from './instrument-content';
import Markdown from './markdown';
import Player from './Player/Player';
import Tone from './tone';
import _ from 'lodash';
import { getMidiBpm } from '../audio/midiTempo';
import reactStringReplace from 'react-string-replace';
import { renderToString } from 'react-dom/server';
import { useGlobalState } from '../state';
import { useMusicEngine } from '../audio/useMusicEngine';

const textColor = '#5e5e5e';
const BEATS_PER_BAR = 4; // assume 4/4 for now
var scoremarks;
const Score = ({ children, ...props }) => {
  const [selectedChordPos, setSelectedChordPos] = useState('');
  // Stable id shared with the Player this Score auto-renders (see midiUrl
  // below), so we can read that exact player's live position back out of
  // the music engine to know which bar is currently playing.
  const playerIdRef = useRef(`score-player-${Math.random().toString(36).slice(2, 10)}`);
  const { players, getParsedMidi } = useMusicEngine();

  var format = props.format === undefined ? "key" : props.format;
  var events = { onChordClick: props.onChordClick, selectedChordPos: selectedChordPos, setSelectedChordPos: setSelectedChordPos };

  scoremarks = props.scoremarks;
  var parentKeyTonic = props.t;
  var parentIsMajorQuality = props.q === "m" ? false : true;
  var previousIsMajor = parentIsMajorQuality;

  let contentOrg = '';
  let re = /(\r\n|\n|\r)/gm;
  let parts = /(\$[A-ES])/gm;
  React.Children.forEach(children, (child) => {
    if (typeof child === 'string') {
      child = child.replace(re, '');
      child = child.replace(parts, function (x) {
        switch (x.trim()) {
          case "$A":
            return '🄰';
          case "$B":
            return '🄱';
          case "$C":
            return '🄲';
          case "$D":
            return '🄳';
          case "$E":
            return '🄴';
          case "$S":
            return '🅂';
          default:
            return x;
        }
      });
      if (format === "text") {
        contentOrg += child;
      }
/*       else if (format === "keytext") {
        contentOrg += child;
      } */
      else {
        //contentOrg += child.replace(/(?<!\[)([A-G])/gm, "!$1"); // Needs lookbehind support, use below instead
        contentOrg += child.replace(/(^|['"\s🄰🄱🄲🄳🄴🅂]{1})([A-G])/gm, "$1!$2"); // Add '!' to all non-roman chords
      }
    }
  });

  if (scoremarks) {
    const bars = contentOrg.split(/;|\|/);
    _.forEach(scoremarks, function (value) {
      const pos = value.pos.split(':');
      let bar = bars[pos[0] - 1];
      if (!bar) {
        return false;
      }
      let barchords = bar.split(',');
      let chord = barchords[pos[1] - 1];
      if (!chord) {
        return false;
      }
      if (value.a) {
        chord = chord.replace(/'.*'/, '');
        chord = (format !== "text") ? `${chord} '${value.a.replace(/([A-G])/gm, "!$1")} '` : `${chord} '${value.a} '`;
      }
      if (value.b) {
        chord = chord.replace(/".*"/, '');
        chord = (format !== "text") ? `${chord} "${value.b.replace(/([A-G])/gm, "!$1")}"` : `${chord} "${value.b}"`;
      }

      barchords[pos[1] - 1] = chord;
      bars[pos[0] - 1] = barchords.join(',');
    });
    contentOrg = bars.join(';');
  }
  let content = contentOrg;

  if (format === "interval") {
    var currentKeyIndex = getKeyIndex(parentKeyTonic, parentIsMajorQuality); // keep state of which key we're in
    const keysRegex = /(?<t>(?:\[t)|(?:\[-?\b(?:[1-7])[PmMd]{1}(?<q>[mM]?)\b))(.*?)(?=\[|$)/g; // Split all content into different keys
    const keysMatches = [...contentOrg.matchAll(keysRegex)];

    const romanChordsRegex = /((I{1,3}|IV|VI{0,2}){1}[#b]?)(\S+)?([|;,]|$|\s)/g; // Find all roman chords
    // For each found key
    const keys = _.map(keysMatches, function (key, idx) {
      let orgKeyContent = key[3];
      const isMajor = key[2] === "m" ? false : true; // quality ('M' or null = major, 'm' = minor)
      const romanChordsMatches = [...orgKeyContent.matchAll(romanChordsRegex)];
      const romanChords = _.map(romanChordsMatches, function (item, idx) {
        return item[1] + (item[3] || "");
      });
      const interval = key[1].substring(1);
      if (interval === 't') {
        // '[t' will reset to global key tonic
        currentKeyIndex = getKeyIndex(parentKeyTonic, parentIsMajorQuality);
        previousIsMajor = parentIsMajorQuality;
      }
      else {
        // Move index to another tonic
        currentKeyIndex = changeKeyIndex(interval, currentKeyIndex);
      }

      // Get chords from roman and apply the key according to index and major|minor
      if (isMajor && !previousIsMajor) {
        currentKeyIndex -= 3;
      }
      if (!isMajor && previousIsMajor) {
        currentKeyIndex += 3;
      }
      const chords = Progression.fromRomanNumerals(getCurrentKey(currentKeyIndex, isMajor), romanChords);
      previousIsMajor = isMajor;
      // Replace original key-content with real chords
      orgKeyContent = orgKeyContent.replace(/[A-G]/gm, '');
      return orgKeyContent.replace(romanChordsRegex, (match, p1, p2, p3, p4) => chords.shift() + p4); // p4=separator
    });

    // Replace original content with real chords for every key
    content = contentOrg.replace(keysRegex, () => keys.shift());
  }
  /* if (format === "keytext") {
    const scoreKeyRegEx = /([A-G]{1}(?:[#b]?))(m?)/;
    let scoreKeyMatch = undefined;
    if (typeof props.k === 'string' && ["A", "B", "C", "D", "E", "F", "G"].includes(props.k.charAt(0))) {
      scoreKeyMatch = props.k?.match(scoreKeyRegEx);
    }
    let scoreKey = "C";
    if (scoreKeyMatch !== undefined) {
      scoreKey = scoreKeyMatch[1];
    }
    contentOrg = `[${scoreKey}${contentOrg}`;
    const scoreOffset = Interval.semitones(Interval.distance(scoreKey, parentKeyTonic));
    // Replace all roman chords defined in different keys
    const keysRegex = /(?<t>(?:\[)[A-G](?:[#b]?))(m?)(.*?)(?=\[|$)/g;
    const keysMatches = [...contentOrg.matchAll(keysRegex)];
    const charChordsRegex = /(?<!\[)(([A-G]){1}[#b]?)(\S+)?([|;,]|$|\s)/g;
    const keys = _.map(keysMatches, function (key, idx) {
      const orgKeyContent = key[3];

      const isMajor = key[2] === "m" ? false : true; // quality ('M' or null = major, 'm' = minor)
      const charChordsMatches = [...orgKeyContent.matchAll(charChordsRegex)];
      let charChords = _.map(charChordsMatches, function (item, idx) {
        return item[1] + (item[3] || "");
      });
      charChords = _.compact(charChords);
      const currentKey = key[1].substring(1);
      const keyOffset = Interval.semitones(Interval.distance(scoreKey, currentKey));
      const transposedKey = getKeyTransposedBySemitones(currentKey, isMajor, scoreOffset);
      const romanChords = Progression.toRomanNumerals(currentKey, charChords);
      const chords = Progression.fromRomanNumerals(transposedKey, romanChords);
      return orgKeyContent.replace(charChordsRegex, (match, p1, p2, p3, p4) => chords.shift() + p4); // p4=separator
    });
    content = contentOrg.replace(keysRegex, () => keys.shift());

  } */
  if (format === "key") {
    const scoreKeyRegEx = /([A-G]{1}(?:[#b]?))(m?)/;
    let scoreKeyMatch = undefined;
    if (typeof props.k === 'string' && ["A", "B", "C", "D", "E", "F", "G"].includes(props.k.charAt(0))) {
      scoreKeyMatch = props.k?.match(scoreKeyRegEx);
    }
    let scoreKey = "C";
    if (scoreKeyMatch !== undefined) {
      scoreKey = scoreKeyMatch[1];
    }
    else{
      return <Notification color="danger">Verify score definition.<br />Missing score attribute k="".</Notification>;
    }

    const scoreOffset = Interval.semitones(Interval.distance(scoreKey, parentKeyTonic));
    // Replace all roman chords defined in different keys
    const keysRegex = /(?<t>(?:\[)[A-G](?:[#b]?))(m?)(.*?)(?=\[|$)/g;
    const keysMatches = [...contentOrg.matchAll(keysRegex)];
    //const romanChordsRegex = /((I{1,3}|IV|VI{0,2}){1}[#b]?)([^\s']+)?([|;,]|$|\s)?/g; 
    const romanChordsRegex = /((I{1,3}|IV|VI{0,2}){1}[#b]?)(\S+)?([|;,]|$|\s)/g;
    const keys = _.map(keysMatches, function (key, idx) {
      const orgKeyContent = key[3];

      const isMajor = key[2] === "m" ? false : true; // quality ('M' or null = major, 'm' = minor)
      const romanChordsMatches = [...orgKeyContent.matchAll(romanChordsRegex)];
      let romanChords = _.map(romanChordsMatches, function (item, idx) {
        return item[1] + (item[3] || "");
      });
      romanChords = _.compact(romanChords);
      const currentKey = key[1].substring(1);
      const keyOffset = Interval.semitones(Interval.distance(scoreKey, currentKey));
      const transposedKey = getKeyTransposedBySemitones(currentKey, isMajor, scoreOffset);
      let chords = Progression.fromRomanNumerals(transposedKey, romanChords);
      let test = Note.simplify("G##");
      chords = _.map(chords, (chord) => {
        chord = chord.replace("#b","").replace("b#","");
        let found = [ ...chord.matchAll(/([ABCDEFGH])(#{2}|b{2})(.*)/gm)];
        if(!found[0])
        {
          return chord;
        }
        let t = `${found[0][1]}${found[0][2]}`;
        let ch = Note.simplify(t)+found[0][3];
        return ch;
      });
      return orgKeyContent.replace(romanChordsRegex, (match, p1, p2, p3, p4) => chords.shift() + p4); // p4=separator
    });
    content = contentOrg.replace(keysRegex, () => keys.shift());
  }

  let defs = null;
  if (content) {
    defs = content.split(/;|\|/);
  }
   if(defs && defs.length>0 && defs[0].trim()==="🄰")
  {
    defs[1] = `${defs[0]} ${defs[1]}`;
    defs.shift();
  } 
  if(defs && defs.length>0 && defs[0]==="")
  {
    defs.shift();
  }

  if(defs && defs[defs.length-1]==="")
  {
    defs.pop();
  }

  if (!defs || defs.length === 0) {
    return <Notification color="danger">Verify score definition.<br />Hint: Don't use blank lines within &lt;score&gt;-tags.</Notification>;
  }

  var lines = new Array();
  var i, j, chunk = 4;
  for (i = 0, j = defs.length; i < j; i += chunk) {
    lines.push(defs.slice(i, i + chunk));
  }

  // A <score midiUrl="..."> (or src=) attribute renders a Player above the
  // score, using the same Player attributes (transpose, bpm, tonic, ...)
  // this score already received. Keeps the score and its player linked, so
  // the current bar can be highlighted while it plays.
  const midiUrl = props.midiUrl ?? props.src;

  // Which bar is currently playing, derived from this score's own player.
  // Assumes 4/4 and that each bar in the text maps 1:1 to a bar in the
  // audio - good enough for a "snap to bar" highlight, not a smooth cursor.
  // Många midifiler spelar upp hela ackordschemat flera varv (låten är
  // längre än score-texten), så takten wrappas runt med modulo istället för
  // att markeringen försvinner efter sista takten.
  let currentBarIndex = null;
  if (midiUrl) {
    const playerState = players[playerIdRef.current];
    if (playerState?.hasSeq && playerState.cursorEnabled !== false) {
      const fileBpm = getMidiBpm(getParsedMidi(playerIdRef.current));
      const effectiveBpm = (playerState.bpmOverride || fileBpm) * (playerState.playbackRate || 1);
      const barDurationSeconds = (60 / effectiveBpm) * BEATS_PER_BAR;
      const bar = Math.floor(playerState.currentTime / barDurationSeconds);
      currentBarIndex = bar >= 0 ? bar % defs.length : null;
    }
  }

  return (
    <>
      {midiUrl && (
        <Player
          playerId={playerIdRef.current}
          midiUrl={midiUrl}
          transpose={props.transpose}
          bpm={props.bpm}
          tonic={props.tonic}
          chooseFile={props.chooseFile}
          midiEditor={props.midiEditor}
          showCursorToggle
        />
      )}
      <Notification color="white" style={{ color: '#4d4d4d', marginTop: 10, marginBottom: 10 }}>
        {lines.map((line, index) => {
          return line.length > 0 ? <Line key={index} events={events} lineindex={index} bars={line} isFirstLine={index === 0} isLastLine={index === lines.length - 1} currentBarIndex={currentBarIndex} /> : null;
        }
        )}
      </Notification>
    </>
  );
}

const Line = ({ events, lineindex, bars, isFirstLine, isLastLine, currentBarIndex }) => {
  //className={partChar && 'line'} data-part={partChar}
  let isSmall = isFirstLine && isLastLine && bars.length < 4;
  return (
    <Columns gap={0} breakpoint="mobile" centered={isSmall} style={{ marginTop: isFirstLine ? 20 : 10, marginBottom: `${isLastLine ? 0 : '1.5em'}` }}>
      {bars.map((bar, index) => {
        const barindex = (lineindex * 4) + index;
        return <Bar key={index} events={events} barindex={barindex} chords={bar} isFirstBar={isFirstLine && index === 0} isLastBar={isLastLine && index === bars.length - 1} isEndBar={index === bars.length - 1} isCurrent={barindex === currentBarIndex} />;
      })}
    </Columns>
  );
}

const Bar = ({ events, barindex, chords, isFirstBar, isLastBar, isEndBar, isCurrent }) => {
  var chs = chords.split(',');
  //let barPadding = 8;
  //let lastColSize = 3;
  const classNames = ''; //`${start?'barstart':''} ${end?'barend':''} ${last?'barlast':''}`;
  return (
    <Columns.Column size={3} style={{ position: 'relative' }}>
      {isCurrent && (
        // Cursor-highlight som en fristående overlay ovanpå allt innehåll, istället för en
        // background/box-shadow på en inre wrapper - .chordNotification har z-index:1 (för
        // ackordfunktionstext ovanför/under takten m.m.) så en vanlig box-shadow hamnar under
        // och blir delvis dold. En egen overlay med högre z-index och manuellt satta kanter
        // kan dessutom sträckas uppåt för att även rama in ackordfunktionstexten ovanför takten,
        // vilket en box-shadow aldrig kan (den följer bara innehållets faktiska storlek).
        <div
          style={{
            position: 'absolute',
            top: -18,
            left: -2,
            right: -2,
            bottom: -4,
            border: '3px solid rgba(255, 128, 0, 0.85)',
            borderRadius: 8,
            zIndex: 5,
            pointerEvents: 'none',
          }}
        />
      )}
      {/* <Columns className={classNames} gap={0} breakpoint="mobile" style={{  paddingLeft: 6, paddingRight: 4, borderLeft: `2px solid black`, borderRight: `${last ? 2 : 0}px solid black` }}> */}
      <Columns className={classNames} gap={0} breakpoint="mobile" >
        <Columns.Column>
          <Columns gap={0} breakpoint="mobile" >
            {chs.map((ch, index) =>
              ch.trim() === "" && index === 0 ?
                <Chord key={0} name={"!"} /> :
                <Chord key={index} events={events} name={ch} barindex={barindex} chordindex={index} isFirstBar={isFirstBar} isEndBar={isEndBar} isLastBar={isLastBar} isLastChordInBar={index === chs.length - 1} />
            )}
            {chs.length === 3 && <Chord key={999} name={""} />}
          </Columns>
        </Columns.Column>
      </Columns>
    </Columns.Column>
  );
}

//const markTextRegex = /C:(?<C>[A-G][b#]?)|Bb?:(?<Bb>[A-G][b#]?)|Eb?:(?<Eb>[A-G][b#]?)/gm;
const markTextRegex = /C?:(?<C>[\w\s]+)(?=Bb)|Bb?:(?<Bb>[\w\s]+)(?=Eb)|Eb?:(?<Eb>[\w\s]+)>/gm;
const Chord = ({ events, name, barindex, chordindex, isFirstBar, isLastBar, isEndBar, isLastChordInBar }) => {

  const [currentInstrument] = useGlobalState('instrument');

  let partChar = null;
  const partChars = ['🄰', '🄱', '🄲', '🄳', '🄴', '🅂']
  if (partChars.some(substr => name.startsWith(substr))) {
    partChar = name.substring(0, '🄰'.length);
    name = name.substring('🄰'.length);
  }

  name = name.trim();
  
  let errorIndication = "";
  if (!["!", "A", "B", "C", "D", "E", "F", "G", "X", "x", "%"].includes(name.charAt(0))) {
    errorIndication = "!";
  }
  name = errorIndication+name.charAt(0).toUpperCase() + name.slice(1);
  
  if (["bb", "##"].includes(name.substring(1, 3))) {
    name = Note.simplify(name.substring(0, 3)) + name.substring(3);
  }
  if (["#b", "b#"].includes(name.substring(1, 3))) {
    name = name.substring(0, 1) + name.substring(3);
  }

  const chordPosition = `${barindex + 1}:${chordindex + 1}`;
  const mark = _.find(scoremarks, { pos: chordPosition });//scoremarks[chordPosition];

  let hasTo = false;
  name = name.replace(/(->)/g, (wholeMatch, textInsideBrackets) => {
    hasTo = true;
    return '';
  });
  const leadsto = mark?.to || hasTo;

  //console.log("MD: "+renderToString(<Tone t="C">I a</Tone>));
  let bc = ''
  name = name.replace(/c:([rgbcmy])/g, (wholeMatch, textInsideBrackets) => {
    bc = textInsideBrackets;
    return '';
  });
  const markColor = getBackgroundColor(mark?.bc || bc);
  //const markColor1 = getBC(mark?.bc);

  let above = ''
  name = name.replace(/'(.+?)'/g, (wholeMatch, textInsideBrackets) => {
    above = textInsideBrackets;
    return '';
  });

  let abovematches = above.matchAll(markTextRegex);
  for (let abovematch of abovematches) {
    if (abovematch.groups[currentInstrument]) {
      above = above.replace(/\<(.*?)\>/, abovematch.groups[currentInstrument]);
    }
  }

  above = translateChordFunction(above);

  let below = '';
  name = name.replace(/"(.+?)"/g, (wholeMatch, textInsideBrackets) => {
    below = textInsideBrackets;
    return '';
  });
  let belowmatches = below.matchAll(markTextRegex);
  for (let belowmatch of belowmatches) {
    if (belowmatch.groups[currentInstrument]) {
      below = below.replace(/\<(.*?)\>/, belowmatch.groups[currentInstrument]);
    }
  }
  /*   if (mark?.b) {
      below = mark?.b;
      let belowmatches = below.matchAll(markTextRegex);
      for (let belowmatch of belowmatches) {
        if (belowmatch.groups[currentInstrument]) {
          below = below.replace(/<.*>/, belowmatch.groups[currentInstrument]);
          break;
        }
      }
    } */
  // <C:F Bb:Eb Eb:Ab> Dorian

  name = name.replace("%", "٪")
    .replace("X", " "/* Note: X is replaced with a "hair space" character to preserve bar height */)
    //.replace("m7-5", "ø")
    //.replace("m7b5", "ø")
    .replace(/b/g, '♭')
    .replace(/#/g, '♯')
    .replace("maj", "Δ")
    .replace("o", "°")
    .replace("dim", "°").trim();


  let chord = reactStringReplace(name, /([ABCDEFG][♭♯]?[mΔ7ø°\s]*.*)/g, (match, index) => {
    match.match(/([ABCDEFG}][♭♯]?[mΔ7ø°\s]*)(.*)/g);
    // https://stackoverflow.com/questions/31906705/alternative-to-deprecated-regexp-n-object-properties
    const chordname = RegExp.$1.trim();
    const chordextensions = RegExp.$2;
    const marksRegEx = /"(?<b>.*)"|'(?<a>.*)'/;
    const marks = marksRegEx.exec(chordextensions);
    //if(marks) {console.log(marks.groups.b + " amd " + marks.groups.a);}
    return <span className={above && 'chordText'} data-above={above} key={chordindex}>{chordname}<sup>{chordextensions}</sup></span>
  });
  const scoreMarksEditMode = events?.onChordClick !== undefined;
  const classNames = `${leadsto ? 'leadsto' : ''} ${chordindex === 0 && !isFirstBar ? 'bs' : ''} ${(chordindex === 0 && isFirstBar) ? 'bf' : ''} ${isLastBar && isLastChordInBar ? 'bl' : ''} ${isEndBar && isLastChordInBar ? 'be' : ''}`;
  return (
    <Columns.Column className={classNames} data-after={leadsto && isLastBar && isLastChordInBar ? '→' : ''} style={{ position: 'relative', marginBottom: 15, paddingTop: 0, paddingBottom: 0, justifyContent: 'flex-end', display: 'flex', flexDirection: 'column' }}>
      <Notification className={`chordNotification ${scoreMarksEditMode ? "chordNotificationScoreMarksEdit" : ''} ${chordPosition === events?.selectedChordPos ? 'selected' : ''}`} onClick={() => { events?.setSelectedChordPos(chordPosition); events?.onChordClick({ chord: chord, pos: chordPosition }); }} color={name.startsWith("!") ? 'danger' : 'white'} style={{ backgroundColor: markColor, padding: 0, paddingLeft: 2, paddingRight: 2, borderRadius: 3, whiteSpace: "nowrap" }}> {/* Chord beat styling: backgroundColor: 'transparent' (will override color), border: '1px solid #f80', */}
        <Heading className={`${below ? 'chord' : ''} ${partChar ? 'partLetter' : ''}  is-size-6-mobile is-size-5-tablet is-size-4-desktop`} data-part={partChar} data-below={below} style={{ color: textColor, textAlign: name === "٪" ? 'center' : 'left' }} renderAs="p">
          {chord}
        </Heading>
      </Notification>
    </Columns.Column>
  );
}
function getBC(bc) {
  switch (bc) {
    case 'r':
      return 'is-danger is-light';
    case 'g':
      return 'is-success is-light';
    case 'b':
      return 'is-info is-light';
    case 'y':
      return 'is-warning is-light';
    case 'o':
      return '#ffbd7a';
    default:
      return 'white';
  }
}
const chordColors = {
  r: '#ffcdcd',//'#ffe3e3',
  g: '#ceffce',
  b: '#ccddff',
  c: '#ccffff',
  m: '#ffbfff',
  y: '#fffabf',
  o: '#ffbd7a',
  w: 'white'
}
function translateChordFunction(aboveText) {
  if (_.includes(["I", "II", "III", "IV", "V", "VI", "VII"], aboveText.toUpperCase())) {
    return "!Use number!";
  }
  return aboveText.replace(/(\d{1})([#b]?)(m?)/, function (_, pos, alt, minor) {
    let roman = "";
    switch (pos) {
      case "1":
        roman = "I";
        break;
      case "2":
        roman = "II";
        break;
      case "3":
        roman = "III";
        break;
      case "4":
        roman = "IV";
        break;
      case "5":
        roman = "V";
        break;
      case "6":
        roman = "VI";
        break;
      case "7":
        roman = "VII";
        break;
    }
    if(minor === "m")
    {
      roman = roman.toLowerCase();
    }
    return `${roman}${alt.replace('b', '♭').replace('#', '♯')}`
  });
}
function getBackgroundColor(bc) {
  switch (bc) {
    case 'r':
      return chordColors.r;
    case 'g':
      return chordColors.g;
    case 'b':
      return chordColors.b;
    case 'c':
      return chordColors.c;
    case 'm':
      return chordColors.m;
    case 'y':
      return chordColors.y;
    case 'o':
      return chordColors.o;
    default:
      return chordColors.w;
  }
}

export default Score;
export { chordColors, translateChordFunction, getBackgroundColor };